import React, { useState } from "react";
import { ArrowRight, Mail } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { submitContact } from "@/lib/api";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";

export default function Contact() {
  const { lang, t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(lang === "en" ? "Please fill required fields" : "Bitte alle Pflichtfelder ausfüllen");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact({ ...form, locale: lang });
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
      toast.success(t.contact.thanks);
    } catch (err) {
      toast.error("Error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-maly-cream min-h-screen">
      <section className="pt-40 lg:pt-48 pb-24 px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 lg:gap-24">
          {/* Left intro */}
          <div className="lg:col-span-5">
            <Reveal>
              <div className="font-body text-[11px] tracking-[0.3em] uppercase text-maly-navy/60 mb-8">{t.contact.overline}</div>
            </Reveal>
            <Reveal delay={100}>
              <h1 className="font-display text-5xl lg:text-7xl font-light tracking-[-0.02em] leading-[1.02]">{t.contact.headline}</h1>
            </Reveal>
            <Reveal delay={200}>
              <p className="font-serif italic text-lg lg:text-xl opacity-70 mt-8 leading-relaxed">{t.contact.sub}</p>
            </Reveal>

            <Reveal delay={300}>
              <ul className="mt-14 space-y-8">
                {[
                  { title: t.contact.pressTitle, mail: t.contact.pressMail },
                  { title: t.contact.atelierTitle, mail: t.contact.atelierMail },
                  { title: t.contact.serviceTitle, mail: t.contact.serviceMail },
                ].map((c, i) => (
                  <li key={i} className="flex items-start gap-4 pb-6 border-b border-maly-navy/10">
                    <Mail size={16} strokeWidth={1.4} className="mt-1 opacity-60" />
                    <div>
                      <div className="font-body text-[10px] tracking-[0.3em] uppercase opacity-60 mb-1">{c.title}</div>
                      <a href={`mailto:${c.mail}`} className="font-display text-xl link-underline">{c.mail}</a>
                    </div>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          {/* Form */}
          <Reveal delay={200} className="lg:col-span-7">
            <form data-testid="contact-form" onSubmit={onSubmit} className="space-y-10 bg-white p-8 lg:p-14 border border-maly-navy/10">
              <div>
                <label className="maly-label">{t.common.name} *</label>
                <input
                  data-testid="contact-name"
                  required
                  className="maly-input"
                  value={form.name}
                  onChange={onChange("name")}
                  placeholder=""
                />
              </div>

              <div>
                <label className="maly-label">{t.common.email} *</label>
                <input
                  data-testid="contact-email"
                  required
                  type="email"
                  className="maly-input"
                  value={form.email}
                  onChange={onChange("email")}
                />
              </div>

              <div>
                <label className="maly-label">{t.common.subject}</label>
                <input
                  data-testid="contact-subject"
                  className="maly-input"
                  value={form.subject}
                  onChange={onChange("subject")}
                />
              </div>

              <div>
                <label className="maly-label">{t.common.message} *</label>
                <textarea
                  data-testid="contact-message"
                  required
                  rows={5}
                  className="maly-input resize-none"
                  value={form.message}
                  onChange={onChange("message")}
                />
              </div>

              <div className="pt-4">
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={submitting}
                  className="btn-primary disabled:opacity-50 w-full sm:w-auto"
                >
                  {submitting ? t.common.sending : t.common.send} <ArrowRight size={14} />
                </button>
              </div>

              {sent && (
                <div data-testid="contact-success" className="font-body text-sm text-maly-sage border-l-2 border-maly-sage pl-4 py-2">
                  {t.contact.thanks}
                </div>
              )}
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
